import { FiClock, FiMapPin, FiCalendar, FiPlay, FiPause, FiLogOut, FiLogIn, FiTrash2 } from 'react-icons/fi'
import { API_URL } from '../config/api'
import { Avatar, Btn, Pill } from '../ui'

/*
 * TimelineFichajeV2 — versión rediseñada del TimelineFichaje original con DS v2.
 * Mantiene la misma API de props.
 *
 * Cambios visuales:
 *   - ui-card en lugar de tarjeta blanca con shadow legacy
 *   - Tipografía display (Instrument Serif) para reloj y tiempo trabajado
 *   - Tokens var(--ink-*), var(--bg-*), var(--line) — sin colores hard-coded gray-*
 *   - Avatar primitive en lugar de img/initials manual
 *   - Pill primitive para estados
 *   - Btn primitive para acciones (en lugar de gradient buttons)
 */

const TimelineFichajeV2 = ({
  horaActual,
  fichajeActivo,
  pausas,
  pausaActiva,
  estaPausado,
  tiempoTranscurrido,
  tiempoTotalDia,
  tiempoPausaActual,
  totalMinutosPausas,
  getTipoPausaInfo,
  formatearDuracion,
  fichajesDelDia,
  geolocalizacion,
  procesando,
  onFicharEntrada,
  onFicharSalida,
  onPausar,
  onReanudar,
  ultimaPausaCreada,
  segundosRestantes,
  onEliminarPausa,
  empleado,
  empresaInfo
}) => {
  const formatearHora = (date) =>
    date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })

  const formatearFechaCorta = (date) =>
    date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })

  const getRolLabel = (rol) =>
    rol === 'super_admin' ? 'Super Administrador' : rol === 'admin' ? 'Administrador' : 'Empleado'

  const getProgressJornada = () => {
    if (!tiempoTotalDia) return 0
    return (tiempoTotalDia.horas * 60 + tiempoTotalDia.minutos) / 480
  }

  const fotoUrl = empleado?.foto_url
    ? `${API_URL.replace('/api', '')}${empleado.foto_url}`
    : null

  // State-based color tokens
  const stateConfig = !fichajeActivo
    ? { dotColor: 'var(--ink-3)', tone: 'neutral', label: 'Fuera de turno', accent: 'var(--ink-2)' }
    : estaPausado
    ? { dotColor: '#F59E0B', tone: 'amber', label: 'En pausa', accent: '#A86A06' }
    : { dotColor: '#10B981', tone: 'emerald', label: 'Activo', accent: '#0F8C66' }

  return (
    <div className="space-y-5">
      {/* ── Tarjeta principal ── */}
      <div className="ui-card overflow-hidden">
        {/* Header: empresa + fecha */}
        <div className="px-6 pt-5 pb-3 flex items-center justify-between">
          <span className="eyebrow">{empresaInfo?.nombre || 'Control horario'}</span>
          <div className="flex items-center gap-1.5 text-[11.5px] text-[var(--ink-3)]">
            <FiCalendar size={12} />
            <span className="capitalize">{formatearFechaCorta(horaActual)}</span>
          </div>
        </div>

        <div className="hairline-grad mx-6" style={{ height: 1 }} />

        {/* Identidad + reloj */}
        <div className="px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              {fotoUrl ? (
                <img
                  src={fotoUrl}
                  alt={empleado?.nombre}
                  className="w-[60px] h-[60px] rounded-card object-cover border border-[var(--line)]"
                />
              ) : (
                <Avatar name={empleado?.nombre || '?'} size={60} />
              )}
              {fichajeActivo && (
                <span
                  className="absolute -bottom-0.5 -right-0.5 inline-flex h-3.5 w-3.5 rounded-full border-2 border-[var(--bg)]"
                  style={{ background: stateConfig.dotColor }}
                />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="font-display text-[22px] leading-tight text-[var(--ink)] truncate">
                {empleado?.nombre || 'Empleado'}
              </h2>
              <p className="text-[11.5px] text-[var(--ink-3)] mt-0.5 font-mono uppercase tracking-wider">
                {getRolLabel(empleado?.rol)}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <Pill tone={stateConfig.tone}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: stateConfig.dotColor }} />
                  {stateConfig.label}
                </Pill>
                {geolocalizacion && (
                  <span className="inline-flex items-center gap-1 text-[10.5px] text-[var(--ink-3)]">
                    <FiMapPin size={11} /> Ubicación verificada
                  </span>
                )}
              </div>
            </div>

            <div className="shrink-0 text-right">
              <div className="font-mono text-[26px] text-[var(--ink)] tnum tracking-tight">
                {formatearHora(horaActual)}
              </div>
              <div className="text-[10.5px] text-[var(--ink-3)] font-mono uppercase tracking-wider">Hora local</div>
            </div>
          </div>
        </div>

        {fichajeActivo ? (
          <>
            <div className="hairline-grad mx-6" style={{ height: 1 }} />

            {/* Tiempo trabajado hoy — display hero */}
            <div className="px-6 py-6 text-center">
              <div className="eyebrow mb-2">Tiempo trabajado hoy</div>
              <div
                className="font-display text-[64px] leading-none tnum"
                style={{ color: stateConfig.accent }}
              >
                {tiempoTotalDia.horas}<span className="text-[40px] font-display-i text-[var(--ink-3)]">h </span>
                {String(tiempoTotalDia.minutos).padStart(2, '0')}<span className="text-[40px] font-display-i text-[var(--ink-3)]">m</span>
              </div>

              <div className="mt-4 flex items-center justify-center gap-5 text-[12px] text-[var(--ink-3)]">
                <span>
                  Entrada{' '}
                  <strong className="text-[var(--ink)] font-mono tnum">
                    {fichajeActivo.hora_entrada?.substring(0, 5)}
                  </strong>
                </span>
                <span className="text-[var(--line-2)]">·</span>
                <span>
                  Jornada{' '}
                  <strong style={{ color: stateConfig.accent }} className="tnum">
                    {Math.min(Math.round(getProgressJornada() * 100), 100)}%
                  </strong>
                </span>
                {totalMinutosPausas > 0 && (
                  <>
                    <span className="text-[var(--line-2)]">·</span>
                    <span>
                      Pausas{' '}
                      <strong className="text-[#A86A06] font-mono tnum">
                        {estaPausado && tiempoPausaActual
                          ? formatearDuracion(totalMinutosPausas + tiempoPausaActual.horas * 60 + tiempoPausaActual.minutos)
                          : formatearDuracion(totalMinutosPausas)}
                      </strong>
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Pausa activa */}
            {estaPausado && pausaActiva && (
              <div className="mx-6 mb-4 px-4 py-3 rounded-soft bg-[rgba(245,158,11,.06)] border border-[rgba(245,158,11,.2)]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <FiPause size={14} className="text-[#A86A06] shrink-0" />
                    <span className="text-[13px] font-medium text-[var(--ink)] truncate">
                      {getTipoPausaInfo(pausaActiva.tipo).nombre}
                    </span>
                    <span className="text-[11px] text-[var(--ink-3)] font-mono">
                      desde {pausaActiva.hora_inicio?.substring(0, 5)}
                    </span>
                  </div>
                  <span className="text-[16px] font-mono tnum font-medium text-[#A86A06]">
                    {tiempoPausaActual.horas > 0 ? `${tiempoPausaActual.horas}h ` : ''}
                    {tiempoPausaActual.minutos}m
                  </span>
                </div>
                {ultimaPausaCreada?.id === pausaActiva.id && segundosRestantes > 0 && (
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => onEliminarPausa(pausaActiva.id)}
                      disabled={procesando}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-[rgba(239,68,68,.08)] text-[#C13030] hover:bg-[rgba(239,68,68,.14)] transition-colors disabled:opacity-50"
                    >
                      <FiTrash2 size={11} />
                      Eliminar ({segundosRestantes}s)
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Pausas cerradas */}
            {pausas.filter(p => p.hora_fin).length > 0 && (
              <div className="mx-6 mb-4 space-y-1.5">
                {pausas.filter(p => p.hora_fin).map((pausa, index) => {
                  const tipoPausa = getTipoPausaInfo(pausa.tipo)
                  const esEliminable = !estaPausado && ultimaPausaCreada?.id === pausa.id && segundosRestantes > 0
                  return (
                    <div
                      key={pausa.id || index}
                      className="flex items-center justify-between px-3 py-2 rounded-soft bg-[var(--bg-2)] text-[12px]"
                    >
                      <div className="flex items-center gap-2 text-[var(--ink-3)]">
                        <span className="font-medium text-[var(--ink-2)]">{tipoPausa.nombre}</span>
                        <span className="font-mono tnum">
                          {pausa.hora_inicio?.substring(0, 5)}–{pausa.hora_fin?.substring(0, 5)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {pausa.duracion_minutos > 0 && (
                          <span className="font-mono tnum text-[var(--ink-2)]">
                            {formatearDuracion(pausa.duracion_minutos)}
                          </span>
                        )}
                        {esEliminable && (
                          <button
                            type="button"
                            onClick={() => onEliminarPausa(pausa.id)}
                            disabled={procesando}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-medium bg-[rgba(239,68,68,.08)] text-[#C13030] hover:bg-[rgba(239,68,68,.14)] transition-colors disabled:opacity-50"
                          >
                            <FiTrash2 size={10} />
                            {segundosRestantes}s
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Acciones */}
            <div className="px-6 pb-6 pt-1">
              <div className="flex items-center justify-center gap-3">
                {estaPausado ? (
                  <>
                    <Btn variant="primary" size="lg" onClick={onReanudar} disabled={procesando}>
                      <FiPlay size={16} />
                      Reanudar
                    </Btn>
                    <Btn variant="ghost" size="lg" onClick={onFicharSalida} disabled={procesando}>
                      <FiLogOut size={16} />
                      Fichar salida
                    </Btn>
                  </>
                ) : (
                  <>
                    <Btn variant="ghost" size="lg" onClick={onPausar} disabled={procesando}>
                      <FiPause size={16} />
                      Pausar
                    </Btn>
                    <Btn variant="primary" size="lg" onClick={onFicharSalida} disabled={procesando}>
                      <FiLogOut size={16} />
                      Fichar salida
                    </Btn>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="px-6 pb-6 pt-2">
            <Btn
              variant="primary"
              size="lg"
              className="w-full"
              onClick={onFicharEntrada}
              disabled={procesando}
            >
              <FiLogIn size={18} />
              {procesando ? 'Procesando…' : 'Fichar entrada'}
            </Btn>
          </div>
        )}
      </div>

      {/* ── Fichajes anteriores del día ── */}
      {fichajesDelDia.length > 0 && (
        <div className="ui-card overflow-hidden">
          <div className="px-5 py-3 flex items-center gap-2 border-b border-[var(--line)]">
            <span className="eyebrow">Fichajes anteriores hoy</span>
            <Pill tone="brand">{fichajesDelDia.length}</Pill>
          </div>
          <div className="divide-y divide-[var(--line)]">
            {fichajesDelDia.map((fichaje, index) => {
              const tiempoTotal = fichaje.tiempo_total || 0
              const tiempoPausas = fichaje.tiempo_pausas || 0
              const tiempoEfectivo = tiempoTotal - tiempoPausas
              const horas = Math.floor(tiempoEfectivo / 60)
              const minutos = tiempoEfectivo % 60
              return (
                <div key={fichaje.id} className="flex items-center justify-between px-5 py-3 text-[13px]">
                  <div className="flex items-center gap-3 min-w-0">
                    {fichajesDelDia.length > 1 && (
                      <span className="text-[10.5px] px-1.5 py-0.5 rounded bg-[var(--bg-2)] text-[var(--ink-3)] font-mono tnum">
                        #{index + 1}
                      </span>
                    )}
                    <span className="font-mono tnum text-[var(--ink-2)]">
                      {fichaje.hora_entrada?.substring(0, 5)}{' — '}
                      {fichaje.hora_salida?.substring(0, 5)}
                      {fichaje.cross_day && (
                        <span className="text-[10.5px] text-[#A86A06] font-medium ml-1">(+1D)</span>
                      )}
                    </span>
                    {tiempoPausas > 0 && (
                      <span className="text-[11px] text-[var(--ink-3)]">
                        {formatearDuracion(tiempoPausas)} pausa
                      </span>
                    )}
                  </div>
                  <span className="font-mono tnum font-medium text-[#0F8C66]">
                    {horas}h {minutos}m
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default TimelineFichajeV2
